import { useState } from "react";
import styled from "styled-components";
import {
  ButtonLink,
  Column,
  DatePicker,
  ModalForm,
  Notice,
  Row,
  Select,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import { formatCompetencia } from "@components/ow";
import { isoToDate, localIsoDate } from "@helpers/localDate";
import { periodFromReference, formatPeriodLabel, closingDayLabel } from "@helpers/timeClock";

const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// O DatePicker com $centralize centraliza no ancestral posicionado mais próximo, e o
// Column da lib é relative: sem position, o calendário centraliza pela linha toda.
const StaticColumn = styled(Column)`
  position: static;
`;

const CLOSING_OPTIONS = [
  { value: "", label: "Último dia do mês (padrão)" },
  ...Array.from({ length: 30 }, (_, index) => ({ value: String(index + 1), label: `Dia ${index + 1}` })),
];

/** 'YYYY-MM' → competência { month, year } do formatCompetencia. */
function competenceOf(reference) {
  const [year, month] = reference.split("-").map(Number);
  return { month, year };
}

/**
 * Linha do período do espelho de ponto, com os dois ajustes que dá para fazer sem
 * sair da página: o dia de fechamento da empresa (fica gravado e vale para as
 * próximas competências) e um período avulso, para um recorte fora do fechamento.
 *
 * O gestor de ponto (EMP com time_clock:read) vê o período e o fechamento, mas só o
 * ADM altera a configuração da empresa.
 */
export default function PeriodControls({
  reference,
  period,
  closingDay,
  isCustom,
  isAdmin,
  onSaveClosingDay,
  onApplyCustomPeriod,
  onResetPeriod,
}) {
  const [showClosing, setShowClosing] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  return (
    <>
      <Line>
        <Text
          $text="<strong>Período:</strong> %s"
          $replaces={[formatPeriodLabel(period.start, period.end)]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />
        {isCustom ? (
          <>
            <StatePill $text="Período personalizado" $variant="blue" $size="small" />
            <ButtonLink $text="Voltar ao padrão" $action={onResetPeriod} $noWidth />
          </>
        ) : (
          <Text $text={`· ${closingDayLabel(closingDay)}`} $variant="paragraph_small_medium" $color="N400" />
        )}
        {isAdmin ? <ButtonLink $text="Alterar dia de fechamento" $action={() => setShowClosing(true)} $noWidth /> : null}
        <ButtonLink $text="Período personalizado" $action={() => setShowCustom(true)} $noWidth />
      </Line>

      {showClosing ? (
        <ClosingDayModal
          reference={reference}
          closingDay={closingDay}
          onSave={onSaveClosingDay}
          onClose={() => setShowClosing(false)}
        />
      ) : null}

      {showCustom ? (
        <CustomPeriodModal
          period={period}
          isCustom={isCustom}
          onApply={(range) => {
            onApplyCustomPeriod(range);
            setShowCustom(false);
          }}
          onReset={() => {
            onResetPeriod();
            setShowCustom(false);
          }}
          onClose={() => setShowCustom(false)}
        />
      ) : null}
    </>
  );
}

/** Dia de fechamento da empresa, com a prévia do período da competência escolhida. */
function ClosingDayModal({ reference, closingDay, onSave, onClose }) {
  const [draft, setDraft] = useState(closingDay ? String(closingDay) : "");
  const [saving, setSaving] = useState(false);

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    setSaving(true);
    const saved = await onSave(draft === "" ? null : parseInt(draft, 10));
    setSaving(false);
    if (saved) onClose();
  }

  const preview = periodFromReference(reference, draft === "" ? null : parseInt(draft, 10));

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title="Dia de fechamento do ponto"
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: "Salvar dia de fechamento", action: save, variant: "primary", isDisabled: saving },
      ]}>
      <Stack>
        <Select
          $idForm="time-clock-closing-day"
          $label="Dia que encerra o período"
          $isRequired
          $options={CLOSING_OPTIONS}
          value={CLOSING_OPTIONS.find((option) => option.value === draft) || CLOSING_OPTIONS[0]}
          onChange={(option) => setDraft(option ? option.value : "")}
        />
        <Text
          $text="Vale para todas as competências. Nenhum dia fica de fora: os dias depois do fechamento entram no período seguinte. Quando o dia não existe no mês (30 em fevereiro), o período fecha no último dia dele."
          $variant="caption_medium"
          $color="N500"
        />
        <Notice
          $variant="gray"
          $title={`Competência de ${formatCompetencia(competenceOf(reference), true)}`}
          $text={`Com esse fechamento, o período fica de ${formatPeriodLabel(preview.start, preview.end)}.`}
        />
      </Stack>
    </ModalForm>
  );
}

/** Período avulso: só vale para esta consulta, sem mudar a configuração da empresa. */
function CustomPeriodModal({ period, isCustom, onApply, onReset, onClose }) {
  const [start, setStart] = useState(period.start);
  const [end, setEnd] = useState(period.end);
  const [errors, setErrors] = useState({});

  function apply() {
    const next = {};
    if (!start) next.start = "Informe o início do período";
    if (!end) next.end = "Informe o fim do período";
    else if (start && end < start) next.end = "O fim não pode ser antes do início";
    setErrors(next);
    if (Object.keys(next).length) return;
    onApply({ start, end });
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={onClose}
      $size="medium"
      $title="Período personalizado"
      $contentBackground="N0"
      $actions={[
        { text: "Voltar ao padrão", action: onReset, variant: "outline", show: isCustom },
        { text: "Cancelar", action: onClose, variant: "outline" },
        { text: "Aplicar período", action: apply, variant: "primary" },
      ]}>
      <Stack>
        <Text
          $text="Use para um recorte fora do fechamento. Não muda a configuração da empresa: vale só para esta consulta."
          $variant="paragraph_small_medium"
          $color="N700"
        />
        <Row $gap={20}>
          <StaticColumn $columns={6}>
            <DatePicker
              $idForm="time-clock-period-start"
              $centralize
              $label="Início"
              $variant="default"
              $isRequired
              $errors={errors.start}
              $date={isoToDate(start)}
              $setDate={(date) => {
                setStart(localIsoDate(date) || "");
                if (errors.start) setErrors((prev) => ({ ...prev, start: "" }));
              }}
            />
          </StaticColumn>
          <StaticColumn $columns={6}>
            <DatePicker
              $idForm="time-clock-period-end"
              $centralize
              $label="Fim"
              $variant="default"
              $isRequired
              $errors={errors.end}
              $minDate={isoToDate(start) || undefined}
              $date={isoToDate(end)}
              $setDate={(date) => {
                setEnd(localIsoDate(date) || "");
                if (errors.end) setErrors((prev) => ({ ...prev, end: "" }));
              }}
            />
          </StaticColumn>
        </Row>
      </Stack>
    </ModalForm>
  );
}
